export type CustomerSegmentationEmphasis = "new" | "returning" | "balanced" | "empty";

export interface CustomerSegmentationModel {
  newCount: number;
  returningCount: number;
  mixTotal: number;
  newSharePct: number;
  returningSharePct: number;
  newSharePctDisplay: string;
  returningSharePctDisplay: string;
  /** Highlights the slightly larger segment in the breakdown UI (no copy). */
  emphasis: CustomerSegmentationEmphasis;
}

const roundOneDecimal = (n: number): number => Math.round(n * 10) / 10;

/**
 * Builds mix percentages and a light emphasis hint for new vs returning customers.
 */
export function buildCustomerSegmentationModel(
  newCustomers: number,
  returningCustomers: number,
): CustomerSegmentationModel {
  const newCount = Number.isFinite(newCustomers) && newCustomers >= 0 ? Math.floor(newCustomers) : 0;
  const returningCount =
    Number.isFinite(returningCustomers) && returningCustomers >= 0 ? Math.floor(returningCustomers) : 0;
  const mixTotal = newCount + returningCount;

  if (mixTotal === 0) {
    return {
      newCount,
      returningCount,
      mixTotal: 0,
      newSharePct: 0,
      returningSharePct: 0,
      newSharePctDisplay: "0",
      returningSharePctDisplay: "0",
      emphasis: "empty",
    };
  }

  const newSharePct = (newCount / mixTotal) * 100;
  const returningSharePct = (returningCount / mixTotal) * 100;
  const newRounded = roundOneDecimal(newSharePct);
  const returningRounded = roundOneDecimal(returningSharePct);

  let emphasis: CustomerSegmentationEmphasis = "balanced";

  if (returningCount === 0 && newCount > 0) {
    emphasis = "new";
  } else if (newCount === 0 && returningCount > 0) {
    emphasis = "returning";
  } else if (returningRounded > newRounded) {
    emphasis = "returning";
  } else if (newRounded > returningRounded) {
    emphasis = "new";
  }

  return {
    newCount,
    returningCount,
    mixTotal,
    newSharePct: newSharePct,
    returningSharePct: returningSharePct,
    newSharePctDisplay: newRounded.toFixed(1),
    returningSharePctDisplay: returningRounded.toFixed(1),
    emphasis,
  };
}
